import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import api, { formatApiError } from "../api";
import { useAffAuth } from "../store";
import { useT } from "../i18n";

const CURRENCIES = ["USD", "EUR", "USDT", "TON", "RUB"];

export default function AffProfile() {
  const { partner, refresh } = useAffAuth();
  const t = useT();
  const [name, setName] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [channels, setChannels] = useState([]);
  const [newChannel, setNewChannel] = useState("");
  const [details, setDetails] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!partner) return;
    setName(partner.name || "");
    setCurrency(partner.display_currency || "USD");
    setChannels(partner.distribution_channels || []);
    setDetails(partner.payout_details || {});
  }, [partner]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/affiliate/me", {
        name,
        display_currency: currency,
        distribution_channels: channels,
        payout_details: details,
      });
      toast.success(t("profile.saved"));
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const addChannel = () => {
    const v = newChannel.trim();
    if (!v) return;
    if (channels.includes(v)) return;
    setChannels([...channels, v]);
    setNewChannel("");
  };

  const removeChannel = (v) => setChannels(channels.filter((c) => c !== v));

  const PAYOUT_FIELDS = [
    { key: "phone", label: t("profile.payout.phone") },
    { key: "card", label: t("profile.payout.card") },
    { key: "bank_account", label: t("profile.payout.bank_account") },
    { key: "ton_wallet", label: t("profile.payout.ton_wallet") },
    { key: "other", label: t("profile.payout.other") },
  ];

  return (
    <div className="space-y-5 max-w-2xl" data-testid="aff-profile-page">
      <header>
        <h1 className="text-2xl font-medium tracking-tight">{t("profile.title")}</h1>
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mt-1">
          {t("profile.subtitle")}
        </p>
      </header>

      <div className="space-y-3 border border-border bg-secondary/10 p-5">
        <Field
          label={t("profile.name")}
          value={name}
          onChange={setName}
          testid="aff-profile-name"
        />

        <ReadOnlyRow
          label={t("profile.email")}
          value={partner?.email}
          hint={t("profile.email_hint")}
        />

        <ReadOnlyRow
          label={t("profile.commission")}
          value={`${partner?.commission_pct ?? 20}%`}
          hint={t("profile.commission_hint")}
        />

        <div>
          <label className="text-[10px] uppercase font-mono tracking-[0.18em] text-muted-foreground block mb-1">
            {t("profile.currency")}
          </label>
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            data-testid="aff-profile-currency"
            className="w-full px-2 py-1.5 bg-background border border-border text-sm focus:outline-none focus:border-foreground/40"
          >
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground mt-1">
            {t("profile.currency_hint")}
          </p>
        </div>

        <div>
          <label className="text-[10px] uppercase font-mono tracking-[0.18em] text-muted-foreground block mb-1">
            {t("profile.channels")}
          </label>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {channels.map((c) => (
              <span
                key={c}
                className="inline-flex items-center gap-1 px-2 py-0.5 border border-border bg-secondary/30 text-xs"
                data-testid={`aff-channel-${c}`}
              >
                {c}
                <button
                  onClick={() => removeChannel(c)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-1.5">
            <input
              value={newChannel}
              onChange={(e) => setNewChannel(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addChannel())}
              placeholder={t("profile.channels_placeholder")}
              data-testid="aff-profile-new-channel"
              className="flex-1 px-2 py-1.5 bg-background border border-border text-sm focus:outline-none focus:border-foreground/40"
            />
            <button
              onClick={addChannel}
              data-testid="aff-profile-add-channel"
              className="px-3 py-1.5 text-sm border border-border hover:border-foreground/40"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-3 border border-border bg-secondary/10 p-5">
        <h2 className="text-sm font-mono uppercase tracking-[0.18em] text-muted-foreground">
          {t("profile.payout_section")}
        </h2>
        <p className="text-xs text-muted-foreground -mt-1">
          {t("profile.payout_intro")}
        </p>
        {PAYOUT_FIELDS.map((f) => (
          <Field
            key={f.key}
            label={f.label}
            value={details[f.key] || ""}
            onChange={(v) => setDetails({ ...details, [f.key]: v })}
            testid={`aff-profile-payout-${f.key}`}
          />
        ))}
      </div>

      <div className="flex justify-end">
        <button
          onClick={save}
          disabled={busy}
          data-testid="aff-profile-save"
          className="px-5 py-2 text-sm bg-foreground text-background hover:opacity-90 disabled:opacity-50"
        >
          {busy ? t("profile.saving") : t("profile.save")}
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase font-mono tracking-[0.18em] text-muted-foreground block mb-1">
        {label}
      </label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full px-2 py-1.5 bg-background border border-border text-sm focus:outline-none focus:border-foreground/40"
      />
    </div>
  );
}

function ReadOnlyRow({ label, value, hint }) {
  return (
    <div>
      <div className="text-[10px] uppercase font-mono tracking-[0.18em] text-muted-foreground mb-1">
        {label}
      </div>
      <div className="px-2 py-1.5 bg-background/40 border border-border/50 text-sm text-muted-foreground">
        {value}
      </div>
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}
